import { Component } from '@theme/component';

const ANIMATION_DURATION = 300;
const DESKTOP_BREAKPOINT = '(min-width: 990px)';

class MediaZoomComponent extends Component {
  requiredRefs = ['dialog'];

  #isClosing = false;
  /** @type {((returnValue?: string) => void) | null} */
  #originalClose = null;
  /** @type {HTMLDialogElement | null} */
  #initializedDialog = null;
  #mediaQuery = window.matchMedia(DESKTOP_BREAKPOINT);
  /** @param {MouseEvent} event */
  #handleDialogClick = (event) => {
    const dialog = this.#initializedDialog;
    if (dialog && event.target === dialog) {
      dialog.close();
    }
  };

  /** @param {Event} event */
  #handleDialogCancel = (event) => {
    if (!this.#isClosing) {
      event.preventDefault();
      this.#initializedDialog?.close();
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.#setupDialog();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setupDialog();
  }

  disconnectedCallback() {
    this.#teardownDialog();
    super.disconnectedCallback();
  }

  #setupDialog() {
    const dialog = this.dialogElement;
    if (!dialog || this.#initializedDialog === dialog) return;

    this.#teardownDialog();

    this.#initializedDialog = dialog;
    const originalClose = dialog.close.bind(dialog);
    this.#originalClose = originalClose;

    dialog.close = (returnValue) => {
      if (dialog.hasAttribute('open') && !this.#isClosing) {
        this.#animateClose(() => {
          originalClose(returnValue);
        });
      } else {
        originalClose(returnValue);
      }
    };

    dialog.addEventListener('click', this.#handleDialogClick);
    dialog.addEventListener('cancel', this.#handleDialogCancel);
  }

  #teardownDialog() {
    const dialog = this.#initializedDialog;
    const originalClose = this.#originalClose;

    if (!dialog || !originalClose) return;

    dialog.close = originalClose;
    dialog.removeEventListener('click', this.#handleDialogClick);
    dialog.removeEventListener('cancel', this.#handleDialogCancel);

    this.#initializedDialog = null;
    this.#originalClose = null;
  }

  /**
   * Opens the zoom overlay and scrolls to the clicked media.
   * Called from on:click="/open" on product media items.
   *
   * @param {Event} event
   */
  open(event) {
    if (!this.#mediaQuery.matches) return;

    const dialog = this.dialogElement;
    if (!dialog || dialog.open) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const clickedItem = target.closest('[data-media-id]');
    const mediaId = clickedItem instanceof HTMLElement ? clickedItem.dataset.mediaId : undefined;

    dialog.showModal();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        dialog.classList.add('is-open');

        if (mediaId) {
          const targetItem = dialog.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
          if (targetItem instanceof HTMLElement) {
            targetItem.scrollIntoView({ behavior: 'auto', block: 'start' });
          }
        }
      });
    });
  }

  /**
   * Closes the zoom overlay with a fade-out animation.
   */
  close() {
    const dialog = this.dialogElement;
    if (!dialog?.open) return;

    dialog.close();
  }

  /**
   * @param {() => void} callback
   */
  #animateClose(callback) {
    this.#isClosing = true;
    this.dialogElement.classList.remove('is-open');

    setTimeout(() => {
      callback();
      this.#isClosing = false;
    }, ANIMATION_DURATION);
  }

  get dialogElement() {
    return /** @type {HTMLDialogElement} */ (this.refs.dialog);
  }
}

if (!customElements.get('media-zoom-component')) {
  customElements.define('media-zoom-component', MediaZoomComponent);
}
